import { ShiftConfig } from 'src/app/shared/models/drilling.models';
import { PeriodBounds, TimeScale } from '../planning.types';

export type ShiftKind = 'morning' | 'day' | 'night';

/** Une colonne de la grille = un shift d'un jour donné. */
export interface ShiftColumn {
  id: string;          // 'YYYY-MM-DD_shiftIndex', ex. '2026-03-24_0'
  date: string;        // 'YYYY-MM-DD'
  shiftIndex: number;
  shiftLabel: string;  // issu de ShiftConfig.shiftLabels
  isWeekend: boolean;
  isToday: boolean;
  /** Vrai quand la fenêtre horaire du shift contient minuit. */
  isNight: boolean;
  kind: ShiftKind;
}

/** Un en-tête de jour, qui chapeaute shiftsPerDay colonnes. */
export interface DayGroup {
  date: string;
  label: string;       // ex. 'Mon, Mar 24'
  weekday: string;     // ex. 'Mon'
  dayNumber: string;   // ex. '24'
  shiftCount: number;  // == ShiftConfig.shiftsPerDay
  isWeekend: boolean;
  isToday: boolean;
}

/**
 * Génère une ShiftColumn par (jour × shift) sur [start, end] inclus.
 */
export function generateColumns(start: Date, end: Date, config: ShiftConfig): ShiftColumn[] {
  const columns: ShiftColumn[] = [];
  const todayStr = toIsoDateString(new Date());

  const current = new Date(start);
  current.setHours(0, 0, 0, 0);

  const endNorm = new Date(end);
  endNorm.setHours(23, 59, 59, 999);

  while (current <= endNorm) {
    const dateStr   = toIsoDateString(current);
    const dow       = current.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isToday   = dateStr === todayStr;

    for (let i = 0; i < config.shiftsPerDay; i++) {
      const startHour = config.shiftStartHours?.[i] ?? 0;
      const isNight   = startHour === 0 || (startHour + config.shiftDurationHours) > 24;
      const kind      = shiftKind(isNight, startHour, config.shiftsPerDay);
      columns.push({
        id:         `${dateStr}_${i}`,
        date:       dateStr,
        shiftIndex: i,
        shiftLabel: config.shiftLabels[i] ?? `Shift ${i + 1}`,
        isWeekend,
        isToday,
        isNight,
        kind,
      });
    }
    current.setDate(current.getDate() + 1);
  }
  return columns;
}

/**
 * Replie les colonnes en en-têtes de jour (un groupe par date distincte).
 * L'échelle pilote la verbosité : 'week' → 'Mon, Mar 24', 'month' → 'Mon 24'.
 * `locale` est la langue de l'application (ex. 'fr') ; à défaut, celle du navigateur.
 */
export function generateDayGroups(columns: ShiftColumn[], scale: TimeScale = 'week', locale?: string): DayGroup[] {
  const groups: DayGroup[] = [];
  const countByDate = new Map<string, number>();

  for (const col of columns) {
    countByDate.set(col.date, (countByDate.get(col.date) ?? 0) + 1);
  }

  const seen = new Set<string>();
  for (const col of columns) {
    if (seen.has(col.date)) { continue; }
    seen.add(col.date);

    // Parsé en heure locale pour éviter le décalage de fuseau
    const [y, m, d] = col.date.split('-').map(Number);
    const dateObj   = new Date(y, m - 1, d);

    groups.push({
      date:       col.date,
      label:      scale === 'month'
        ? dateObj.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' })
        : dateObj.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' }),
      weekday:    dateObj.toLocaleDateString(locale, { weekday: 'short' }),
      dayNumber:  String(d).padStart(2, '0'),
      shiftCount: countByDate.get(col.date) ?? 1,
      isWeekend:  col.isWeekend,
      isToday:    col.isToday,
    });
  }
  return groups;
}

/**
 * Icône du shift : lune s'il contient minuit, lever de soleil pour le premier
 * shift d'une journée à trois postes ou plus, soleil sinon.
 */
function shiftKind(isNight: boolean, startHour: number, shiftsPerDay: number): ShiftKind {
  if (isNight) { return 'night'; }
  return shiftsPerDay >= 3 && startHour < 10 ? 'morning' : 'day';
}

/**
 * Bornes canoniques d'une période : lundi → dimanche en semaine,
 * 1er → dernier jour en mois. Identique au calcul des pages legacy.
 */
export function computeBounds(scale: TimeScale, anchor: Date): PeriodBounds {
  if (scale === 'month') {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end   = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  const dow         = anchor.getDay(); // 0 = dimanche
  const daysToMonday = dow === 0 ? -6 : 1 - dow;
  const start = new Date(anchor);
  start.setDate(anchor.getDate() + daysToMonday);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** Décale une date d'ancrage d'une période entière, en avant ou en arrière. */
export function shiftAnchor(scale: TimeScale, anchor: Date, direction: -1 | 1): Date {
  if (scale === 'month') {
    return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  }
  const next = new Date(anchor);
  next.setDate(next.getDate() + direction * 7);
  return next;
}

/** Option du sélecteur de mois, identifiée par 'YYYY-MM'. */
export interface MonthOption {
  id: string;
  label: string;
}

export function toMonthId(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function monthIdToDate(monthId: string): Date | null {
  const [year, month] = monthId.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) { return null; }
  return new Date(year, month - 1, 1);
}

/**
 * Construit la liste des mois proposés : les douze derniers, le mois courant et
 * les deux suivants, du plus récent au plus ancien. Le mois de la période
 * affichée y est ajouté s'il sort de cette fenêtre. Libellés dans la langue `locale`.
 */
export function buildMonthOptions(anchor: Date, locale?: string): MonthOption[] {
  const today  = new Date();
  const months = new Map<string, Date>();

  for (let offset = -12; offset <= 2; offset++) {
    const date = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    months.set(toMonthId(date), date);
  }

  const anchorMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  months.set(toMonthId(anchorMonth), anchorMonth);

  return [...months.entries()]
    .sort(([, a], [, b]) => b.getTime() - a.getTime())
    .map(([id, date]) => ({
      id,
      label: date.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
    }));
}

export function toIsoDateString(d: Date): string {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
